import { createContext, useContext, useEffect, useRef } from "react";

export interface DialogTitleAction {
  label: string;
  onAdd: () => void;
}

/**
 * Lets a nested editor report its own "add a new item" action up to the dialog's title row,
 * without threading a prop through every editor + tab-panel pair.
 */
const DialogTitleActionContext = createContext<((action: DialogTitleAction | null) => void) | null>(
  null
);

export const DialogTitleActionProvider = DialogTitleActionContext.Provider;

/**
 * Registers `action`, cleared automatically on unmount so switching to a tab with no add
 * action doesn't leave a stale button behind.
 *
 * `onAdd` is read through a ref rather than depended on directly: the `addX` functions passed
 * in here are fresh closures on every render, so depending on `onAdd` directly re-ran this
 * effect every render, which called `setAction` in the parent, which re-rendered this
 * component with a new closure, re-running the effect again — an infinite "Maximum update
 * depth exceeded" loop. `label` alone is a stable dependency.
 */
export function useDialogTitleAction(action: DialogTitleAction | null) {
  const setAction = useContext(DialogTitleActionContext);
  const onAddRef = useRef(action?.onAdd);
  onAddRef.current = action?.onAdd;

  useEffect(() => {
    if (!setAction) return;
    if (!action) {
      setAction(null);
      return;
    }
    setAction({ label: action.label, onAdd: () => onAddRef.current?.() });
    return () => setAction(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setAction, action?.label]);
}
